import React, { useState } from "react";

import moment from "moment-timezone";
import { Link as RouterLink } from "react-router-dom";

import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Grid from "@material-ui/core/Grid";
import Link from "@material-ui/core/Link";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import { green, red, yellow } from "@material-ui/core/colors";
import Paper from "../../UI/MainPaper";
import CustomerComboBox from "../../FormFields/CustomerComboBox";
import { ActionButton } from "./SectionTitle";

import DoneIcon from "@material-ui/icons/Done";
import EditIcon from "@material-ui/icons/Edit";
import EmailIcon from "@material-ui/icons/Email";
import ErrorIcon from "@material-ui/icons/Error";
import HelpIcon from "@material-ui/icons/Help";
import LaunchIcon from "@material-ui/icons/Launch";
import PhoneIcon from "@material-ui/icons/Phone";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3),
  },
  layout: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(3),
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
  },
  label: {
    color: theme.palette.text.secondary,
    fontSize: "0.7rem",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  factValue: {
    fontWeight: 600,
    fontSize: "1rem",
  },
  facts: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  contact: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing(0.25),
    margin: theme.spacing(0.5, 0),
  },
  link: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  green: {
    color: theme.palette.getContrastText(green[600]),
    backgroundColor: green[600],
  },
  red: {
    color: theme.palette.getContrastText(red[500]),
    backgroundColor: red[500],
  },
  yellow: {
    color: theme.palette.getContrastText(yellow[600]),
    backgroundColor: yellow[600],
  },
  cancellationButton: {
    backgroundColor: red[600],
    color: theme.palette.getContrastText(red[600]),
    "&:hover": {
      backgroundColor: red[800],
    },
  },
}));

const LinkRouter = (props) => <Link {...props} component={RouterLink} />;

const STATUS_OPTIONS = {
  order: ["PENDING", "COMPLETE", "CANCELLED"],
  reservation: ["PENDING", "RESERVED", "CANCELLED"],
};

const statusLook = (status, classes) =>
  status === "COMPLETE" || status === "RESERVED"
    ? { icon: <DoneIcon />, className: classes.green }
    : status === "CANCELLED"
    ? { icon: <ErrorIcon />, className: classes.red }
    : { icon: <HelpIcon />, className: classes.yellow };

// Status chip that opens a small dialog to change the status
const StatusChip = ({ label, status, options, onSubmit }) => {
  const classes = useStyles();
  const [isOpen, setOpen] = useState(false);
  const [value, setValue] = useState(status);
  const look = statusLook(status, classes);

  const open = () => {
    setValue(status);
    setOpen(true);
  };

  return (
    <>
      <Chip
        icon={look.icon}
        className={look.className}
        label={`${label}: ${status}`}
        onClick={open}
        onDelete={open}
        deleteIcon={<EditIcon />}
      />
      <Dialog
        open={isOpen}
        onClose={() => setOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Edit {label} Status</DialogTitle>
        <DialogContent>
          <FormControl fullWidth>
            <Select value={value} onChange={(e) => setValue(e.target.value)}>
              {options.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            disabled={value === status}
            onClick={() => {
              onSubmit(value);
              setOpen(false);
            }}
          >
            Update {label} Status
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const CustomerBlock = ({
  customer_id,
  customer_name,
  customer_email,
  customer_phone,
  onSubmit,
}) => {
  const classes = useStyles();
  const [isOpen, setOpen] = useState(false);
  const [customer, setCustomer] = useState({ customer_id, customer_name });
  const [isSaving, setSaving] = useState(false);

  const handleSubmit = async () => {
    setSaving(true);
    await onSubmit(customer.customer_id);
    setSaving(false);
    setOpen(false);
  };

  return (
    <div>
      <Typography className={classes.label}>Customer</Typography>
      <Box display="flex" alignItems="center" style={{ gap: 12 }}>
        <Typography className={classes.factValue}>{customer_name}</Typography>
        <ActionButton icon={<EditIcon />} onClick={() => setOpen(true)}>
          Change
        </ActionButton>
      </Box>
      {(customer_email || customer_phone) && (
        <div className={classes.contact}>
          {customer_email && (
            <Link href={`mailto:${customer_email}`} className={classes.link}>
              <EmailIcon style={{ fontSize: 14 }} />
              <Typography variant="body2">{customer_email}</Typography>
            </Link>
          )}
          {customer_phone && (
            <Link href={`tel:${customer_phone}`} className={classes.link}>
              <PhoneIcon style={{ fontSize: 14 }} />
              <Typography variant="body2">{customer_phone}</Typography>
            </Link>
          )}
        </div>
      )}
      <LinkRouter
        to={`/sales/customers/${customer_id}`}
        className={classes.link}
      >
        <LaunchIcon style={{ fontSize: 14 }} />
        <Typography variant="caption">Go to Customer page</Typography>
      </LinkRouter>

      <Dialog
        open={isOpen}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change Customer</DialogTitle>
        <DialogContent>
          <CustomerComboBox
            defaultValue={{ customer_id, customer_name }}
            onChange={setCustomer}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={
              isSaving ||
              !customer?.customer_id ||
              customer.customer_id === customer_id
            }
            onClick={handleSubmit}
          >
            Save Customer
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const SendEmailsButton = ({ onSubmit, isLoading }) => {
  const [isOpen, setOpen] = useState(false);
  const [recipients, setRecipients] = useState({
    sendGuest: true,
    sendHotel: true,
    sendAdmin: true,
  });

  const toggle = (key) => () =>
    setRecipients((current) => ({ ...current, [key]: !current[key] }));

  return (
    <>
      <ActionButton
        icon={
          isLoading ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <EmailIcon />
          )
        }
        disabled={isLoading}
        onClick={() => setOpen(true)}
      >
        Send Emails
      </ActionButton>
      <Dialog
        open={isOpen}
        onClose={() => setOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Send Confirmation Emails</DialogTitle>
        <DialogContent>
          {[
            ["sendGuest", "Send to Guest"],
            ["sendHotel", "Send to Hotel"],
            ["sendAdmin", "Send to Admin"],
          ].map(([key, label]) => (
            <FormControlLabel
              key={key}
              style={{ display: "flex" }}
              control={
                <Checkbox checked={recipients[key]} onChange={toggle(key)} />
              }
              label={label}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!Object.values(recipients).some(Boolean)}
            onClick={() => {
              onSubmit(recipients);
              setOpen(false);
            }}
          >
            Send Emails
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const CancellationEmailButton = ({ onClick, isLoading }) => (
  <ActionButton
    tone="danger"
    icon={
      isLoading ? <CircularProgress size={16} color="inherit" /> : <EmailIcon />
    }
    disabled={isLoading}
    onClick={onClick}
  >
    Send Cancellation Email
  </ActionButton>
);

// Confirms sending the cancellation email. `afterCancel` is when it opens on
// its own right after the order was set to Cancelled.
const CancellationEmailDialog = ({ open, afterCancel, onClose, onSend }) => {
  const classes = useStyles();

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        {afterCancel
          ? "Order cancelled. Send the cancellation email now?"
          : "Are you sure you want to send a cancellation email?"}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          The guest will receive a notification in their email letting them know
          their reservation has been cancelled.
          {afterCancel &&
            " You can also send it later from the Send Cancellation Email button."}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {afterCancel ? "Not Now" : "Cancel"}
        </Button>
        <Button
          variant="contained"
          className={classes.cancellationButton}
          onClick={() => {
            onClose();
            onSend();
          }}
        >
          Send Cancellation Email
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// One labeled fact in the overview row
const Fact = ({ label, children, secondary }) => {
  const classes = useStyles();

  return (
    <div>
      <Typography className={classes.label}>{label}</Typography>
      <Typography className={classes.factValue}>{children || "—"}</Typography>
      {secondary && (
        <Typography variant="caption" color="textSecondary">
          {secondary}
        </Typography>
      )}
    </div>
  );
};

// Overview at the top of the order page: what the order is, where it stands,
// the order-level actions, and the booking at a glance.
const OrderHeader = ({
  orderId,
  order,
  onStatusSubmit,
  onReservationStatusSubmit,
  onCustomerSubmit,
  onSendEmailSubmit,
  isLoadingEmail,
  onSendCancellationEmailSubmit,
  isLoadingCancellationEmail,
}) => {
  const classes = useStyles();
  const {
    type,
    order_status,
    reservation_status,
    date_order_placed,
    customer_id,
    customer_name,
    customer_email,
    customer_phone,
    check_in,
    check_out,
  } = order;
  const isPackage = type === "package";
  // null, "manual" (from the button) or "afterCancel" (offered on cancel)
  const [cancellationDialog, setCancellationDialog] = useState(null);

  const handleOrderStatusSubmit = async (status) => {
    const succeeded = await onStatusSubmit(status);
    if (
      succeeded &&
      isPackage &&
      status === "CANCELLED" &&
      order_status !== "CANCELLED"
    ) {
      setCancellationDialog("afterCancel");
    }
  };
  const stay = isPackage ? order.package : order.condo;

  return (
    <Paper className={classes.paper}>
      <div className={classes.layout}>
        <div>
          <Box display="flex" alignItems="center" style={{ gap: 12 }}>
            <Typography component="h1" variant="h5">
              Order #{orderId}
            </Typography>
            {type && (
              <Chip size="small" color="secondary" label={type.toUpperCase()} />
            )}
          </Box>
          <Typography variant="body2" color="textSecondary">
            Placed{" "}
            {moment(date_order_placed).tz("America/Chicago").format("lll")}
          </Typography>
          <div className={classes.chips}>
            {order_status && (
              <StatusChip
                key={`order-${order_status}`}
                label="Order"
                status={order_status}
                options={STATUS_OPTIONS.order}
                onSubmit={handleOrderStatusSubmit}
              />
            )}
            {reservation_status && (
              <StatusChip
                key={`reservation-${reservation_status}`}
                label="Reservation"
                status={reservation_status}
                options={STATUS_OPTIONS.reservation}
                onSubmit={onReservationStatusSubmit}
              />
            )}
          </div>
        </div>

        {isPackage && (
          <div className={classes.actions}>
            <SendEmailsButton
              onSubmit={onSendEmailSubmit}
              isLoading={isLoadingEmail}
            />
            {order_status === "CANCELLED" && (
              <CancellationEmailButton
                onClick={() => setCancellationDialog("manual")}
                isLoading={isLoadingCancellationEmail}
              />
            )}
          </div>
        )}
      </div>

      {isPackage && (
        <CancellationEmailDialog
          open={Boolean(cancellationDialog)}
          afterCancel={cancellationDialog === "afterCancel"}
          onClose={() => setCancellationDialog(null)}
          onSend={onSendCancellationEmailSubmit}
        />
      )}

      <Grid container spacing={3} className={classes.facts}>
        <Grid item xs={12} sm={6} md={4}>
          <CustomerBlock
            key={customer_id}
            customer_id={customer_id}
            customer_name={customer_name}
            customer_email={customer_email}
            customer_phone={customer_phone}
            onSubmit={onCustomerSubmit}
          />
        </Grid>
        <Grid item xs={6} sm={3} md={4}>
          <Fact label="Check In">{moment(check_in).utc().format("ll")}</Fact>
        </Grid>
        <Grid item xs={6} sm={3} md={4}>
          <Fact
            label="Check Out"
            secondary={
              stay?.night_stay
                ? `${stay.night_stay} night${stay.night_stay === 1 ? "" : "s"}`
                : null
            }
          >
            {moment(check_out).utc().format("ll")}
          </Fact>
        </Grid>
        {isPackage ? (
          <>
            <Grid item xs={12} sm={6} md={4}>
              <Fact label="Package">{stay?.package_name}</Fact>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Fact label="Hotel" secondary={stay?.hotel_address}>
                {stay?.hotel_name}
              </Fact>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Fact label="Room">
                {stay?.room_name && `${stay.room_qty} × ${stay.room_name}`}
              </Fact>
            </Grid>
          </>
        ) : (
          <Grid item xs={12} md={8}>
            <Fact label="Condo" secondary={stay?.condo_address}>
              {stay?.condo_name}
            </Fact>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default OrderHeader;
